import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { NetworkError } from 'modules/core/lib/errors'
import i18n from 'modules/core/lib/i18n'

import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import type IncidenceDetail from 'modules/bookkeepers/store/models/IncidenceDetail'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import PayrollIntegration, {
  PayrollIntegrationType,
} from 'modules/payroll_integrations/lib/catalog'
import IncidenceSync, {
  Status,
  Sync,
} from 'modules/payroll_integrations_datev_api/lib/IncidenceSync/store'
import authService, {
  Status as AuthStatus,
} from 'modules/payroll_integrations_datev_api/lib/authService'
import CompanyInstallationsChecker from 'modules/payroll_integrations_kombo/lib/CompanyInstallations/checker'

import Contract from './Contract'
import Errored from './Errored'
import Hiring from './Hiring'
import Leave from './Leave'
import NotAuthenticated from './NotAuthenticated'
import PersonalData from './PersonalData'

interface Props {
  incidenceType: keyof typeof INCIDENCE_TYPES
  incidenceDetail: IncidenceDetail
  companyId?: string
}

const selectComponent = (type: keyof typeof INCIDENCE_TYPES | null) => {
  switch (type) {
    case INCIDENCE_TYPES.hiring:
      return Hiring
    case INCIDENCE_TYPES.contract:
      return Contract
    case INCIDENCE_TYPES.name:
    case INCIDENCE_TYPES.address:
    case INCIDENCE_TYPES.gender:
    case INCIDENCE_TYPES.nationality:
    case INCIDENCE_TYPES.phone_number:
    case INCIDENCE_TYPES.tax_id:
      return PersonalData
    case INCIDENCE_TYPES.sick:
    case INCIDENCE_TYPES.parental:
    case INCIDENCE_TYPES.custom_leave:
      return Leave
    default:
      return null
  }
}

const Incidence = ({ incidenceType, incidenceDetail, companyId }: Props) => {
  const incidenceId = incidenceDetail.attributes.incidenceId
  const Component = selectComponent(incidenceType)
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.NotAvailable)
  const [status, setStatus] = useState<Sync | null>(null)
  const [error, setError] = useState<string | null>(null)
  const isBookkeeperView = useBookkeeperView()
  const [datevApiInstalled, setDatevApiInstalled] = useState<boolean>(false)
  const [datevLugInstalled, setDatevLugInstalled] = useState<boolean>(false)
  const [payrollIntegration, setPayrollIntegration] = useState<PayrollIntegrationType | null>(null)
  const isHiringIncidence = incidenceType === INCIDENCE_TYPES.hiring
  const isUserAuthenticated = authStatus === AuthStatus.Authenticated

  const fetchAuthStatus = async () => {
    setAuthStatus(
      await authService.getStatus({
        companyId: companyId,
        bookkeeperView: isBookkeeperView,
      })
    )
  }

  const connect = async () => {
    await authService.connect(authStatus, companyId)
    fetchAuthStatus()
  }

  const { loading } = useAsyncData(async () => {
    const isDatevApiInstalled = await CompanyInstallationsChecker.isDatevApiInstalled()
    const isDatevLugInstalled = await CompanyInstallationsChecker.isDatevLugInstalled()

    if (!(isDatevApiInstalled || isDatevLugInstalled)) return
    setDatevApiInstalled(isDatevApiInstalled)
    setDatevLugInstalled(isDatevLugInstalled)
    await fetchAuthStatus()
  }, [companyId])

  useEffect(() => {
    if (!(datevApiInstalled || datevLugInstalled)) return

    if (datevApiInstalled) {
      setPayrollIntegration(PayrollIntegration.datevApi)
    } else if (datevLugInstalled) {
      setPayrollIntegration(PayrollIntegration.datevLugApi)
    }

    if (Component === null) return

    const checkStatus = async () => {
      if (!isHiringIncidence && !isUserAuthenticated) return
      if (payrollIntegration === null) return

      const incidenceStatus = await IncidenceSync.status({
        companyId,
        incidenceId,
        integration: payrollIntegration,
        isBookkeeperView: isBookkeeperView,
      })
      setStatus(incidenceStatus)
    }
    checkStatus()
  }, [
    datevApiInstalled,
    datevLugInstalled,
    payrollIntegration,
    Component,
    companyId,
    incidenceId,
    isBookkeeperView,
    authStatus,
    isHiringIncidence,
    isUserAuthenticated,
  ])

  const { running: syncing, performAction: onSync } = useAsyncAction(
    async () => {
      try {
        if (payrollIntegration === null) return

        const sync = await IncidenceSync.sync({
          companyId,
          incidenceId: incidenceDetail.attributes.incidenceId,
          integration: payrollIntegration,
          isBookkeeperView: isBookkeeperView,
        })

        setStatus(sync)
        setError(null)
      } catch (e: unknown) {
        let message: string
        if (e instanceof NetworkError) {
          message = ''
        } else if (e instanceof Error) {
          message = e.message
        } else {
          message = JSON.stringify(error)
        }

        setError(message)
        throw e
      }
    },
    {
      successMessage: i18n.t('payroll_integrations.incidences.datev_api.sync.success_message'),
    }
  )

  const show =
    !loading &&
    payrollIntegration !== null &&
    (datevApiInstalled || datevLugInstalled) &&
    authStatus !== AuthStatus.NotAvailable &&
    Component !== null

  if (!show) return <></>

  if (error !== null)
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <Errored error={error} syncing={syncing} onSync={onSync} />
      </Box>
    )

  // For Hiring incidences we want to show the status of an incidence even before the user being logged in
  if (status?.status === Status.Unavailable && !isUserAuthenticated && isHiringIncidence)
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <Component sync={status} syncing={syncing} onSync={onSync} />
      </Box>
    )

  if (authStatus != AuthStatus.Authenticated)
    return (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <NotAuthenticated connect={connect} />
      </Box>
    )

  return (
    status && (
      <Box paddingTop="s24" paddingLeft="s24" paddingRight="s40">
        <Component sync={status} syncing={syncing} onSync={onSync} />
      </Box>
    )
  )
}

export default Incidence
